import React, { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";

export const EXPRESSION_PRESETS = ["Rire", "Respiration", "Pause 1s", "Nostalgique"];

export function PauseCircleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M3.94584 5.45489C3.94584 5.10343 4.23074 4.81852 4.5822 4.81852C4.93365 4.81852 5.21856 5.10343 5.21856 5.45489V10.5458C5.21856 10.8972 4.93365 11.1821 4.5822 11.1821C4.23074 11.1821 3.94584 10.8972 3.94584 10.5458V5.45489ZM1.40039 7.36397C1.40039 7.01252 1.6853 6.72761 2.03675 6.72761C2.3882 6.72761 2.67311 7.01252 2.67311 7.36397V8.63669C2.67311 8.98814 2.3882 9.27305 2.03675 9.27305C1.6853 9.27305 1.40039 8.98814 1.40039 8.63669V7.36397ZM6.49128 2.27308C6.49128 1.92163 6.77619 1.63672 7.12764 1.63672C7.4791 1.63672 7.764 1.92163 7.764 2.27308V12.4549C7.764 12.8063 7.4791 13.0912 7.12764 13.0912C6.77619 13.0912 6.49128 12.8063 6.49128 12.4549V2.27308ZM9.03673 3.5458C9.03673 3.19435 9.32163 2.90944 9.67309 2.90944C10.0245 2.90944 10.3094 3.19435 10.3094 3.5458V13.7276C10.3094 14.079 10.0245 14.3639 9.67309 14.3639C9.32163 14.3639 9.03673 14.079 9.03673 13.7276V3.5458ZM11.5822 5.45489C11.5822 5.10343 11.8671 4.81852 12.2185 4.81852C12.57 4.81852 12.8549 5.10343 12.8549 5.45489V10.5458C12.8549 10.8972 12.57 11.1821 12.2185 11.1821C11.8671 11.1821 11.5822 10.8972 11.5822 10.5458V5.45489ZM14.1276 7.36397C14.1276 7.01252 14.4125 6.72761 14.764 6.72761C15.1154 6.72761 15.4003 7.01252 15.4003 7.36397V8.63669C15.4003 8.98814 15.1154 9.27305 14.764 9.27305C14.4125 9.27305 14.1276 8.98814 14.1276 8.63669V7.36397Z" fill="currentColor"/>
    </svg>
  );
}

export function DraggableExpressionChip({
  label,
  onPick,
}: {
  label: string;
  onPick?: (raw: string) => void;
}) {
  return (
    <span
      onClick={() => onPick?.(` [${label}]`)}
      className="inline-flex min-h-6 h-auto items-center rounded-[4px] border border-[#623DC7] bg-[#E1D6FF] px-2 py-1 text-[12px] font-medium leading-snug text-[#2F1E64] cursor-grab active:cursor-grabbing select-none"
    >
      <PauseCircleIcon className="mr-1.5 h-3.5 w-3.5 shrink-0" />
      {label}
    </span>
  );
}

export function FloatingChip({ rawTag }: { rawTag: string }) {
  const trimmed = rawTag.trim();
  let label = trimmed;
  let chipClass =
    "inline-flex items-center rounded-[4px] border border-[#623DC7] bg-[#E1D6FF] px-2 py-1 text-[12px] font-medium text-[#2F1E64] shadow-md opacity-90";
  let isSound = false;
  if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
    label = trimmed.slice(1, -1);
  } else if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
    label = "Effet Sonore";
    isSound = true;
    chipClass =
      "inline-flex items-center rounded-[4px] border border-[#C8009C] bg-[#FDEBF9] px-2 py-1 text-[12px] font-medium text-[#7D005F] shadow-md opacity-90";
  }
  return (
    <span className={chipClass}>
      {!isSound && <PauseCircleIcon className="mr-1.5 h-3.5 w-3.5 shrink-0" />}
      {label}
    </span>
  );
}

export function ExpressionVocale({ onPick }: { onPick?: (raw: string) => void }) {
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState("");
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div className="relative" ref={wrapperRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex h-6 items-center gap-1.5 rounded-[4px] border border-[#623DC7] bg-[#E1D6FF] px-2 text-[12px] font-medium text-[#2F1E64] select-none hover:bg-[#cec0f9]"
      >
        <PauseCircleIcon className="h-3.5 w-3.5 shrink-0" />
        Expression vocale
        <ChevronDown className={`h-3 w-3 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute left-0 top-full z-30 mt-1 flex w-[200px] flex-col gap-2 rounded-[12px] border border-[#E2E8F0] bg-white p-3 shadow-[0_4px_16px_rgba(0,0,0,0.12)]">
          <input
            type="text"
            value={custom}
            onChange={(e) => setCustom(e.target.value)}
            placeholder="Écrire une expression…"
            className="w-full rounded-[8px] border border-[#E2E8F0] bg-[#F8FAFC] px-2 py-1.5 text-[12px] outline-none placeholder:text-[#94a3b8] focus:border-[#623DC7]"
          />
          {custom.trim() && (
            <div className="flex">
              <DraggableExpressionChip label={custom.trim()} onPick={onPick} />
            </div>
          )}
          <div className="flex max-h-[140px] flex-col gap-1.5 overflow-y-auto">
            {EXPRESSION_PRESETS.map((expr) => (
              <DraggableExpressionChip key={expr} label={expr} onPick={onPick} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
